import { useState, useRef, useEffect } from 'react';
import { get } from 'lodash';
import { createWidgetSubmission, uploadFile, fetchFileUploadStatus, deleteFileUpload } from '../actions';

/**
 * Custom hook to handle file uploads with polling for scan status
 * @param {Object} coreToken - The core token object with token
 * @returns {Object} File upload handlers and state
 */
const useFileUpload = (coreToken) => {
  const recordType = 'WidgetSubmission';
  const pollingInterval = 5000;

  const [hasPendingFiles, setHasPendingFiles] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [fileStatuses, setFileStatuses] = useState([]);

  const pollingIntervalRef = useRef(null);
  const fileUploadStatusesRef = useRef({});
  const widgetSubmissionIdRef = useRef(null);
  const uploadedFilesRef = useRef(new Set());
  const tempIdToUploadIdMapRef = useRef({});

  // Generate a unique identifier for a file
  const getFileId = (file) => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const updateFileStatusesFromResponse = (newStatuses) => {
    setFileStatuses((prevStatuses) => {
      return prevStatuses.map((fileStatus) => {
        const serverId = fileStatus.serverId;

        if (!serverId) {
          return fileStatus;
        }

        const newStatus = newStatuses[serverId];
        if (newStatus) {
          return {
            ...fileStatus,
            status: newStatus,
          };
        }
        return fileStatus;
      });
    });
  };

  const processStatusResponse = (data) => {
    const newStatuses = {};
    data.forEach((item) => {
      newStatuses[item.id] = get(item, 'attributes.status');
    });
    return newStatuses;
  };

  const checkFileStatuses = async (token, submissionId) => {
    try {
      const statusResponse = await fetchFileUploadStatus(
        token,
        recordType,
        submissionId
      );

      if (!statusResponse.error && statusResponse.data) {
        const data = get(statusResponse, 'data.data', []);
        const newStatuses = processStatusResponse(data);

        fileUploadStatusesRef.current = newStatuses;
        const hasPendingStatus = Object.values(newStatuses).some(
          (status) => status === 'pending'
        );
        setHasPendingFiles(hasPendingStatus);
        updateFileStatusesFromResponse(newStatuses);

        if (!hasPendingStatus) {
          stopPolling();
        }
      }
    } catch (error) {
      console.error('Error checking file upload statuses', error);
    }
  };

  const startPolling = (token, submissionId) => {
    // Don't start if already polling
    if (pollingIntervalRef.current) {
      return;
    }

    // Check if there are any pending files
    const hasPending = Object.values(fileUploadStatusesRef.current).some(
      (status) => status === 'pending'
    );

    if (!hasPending) {
      stopPolling();
      return;
    }

    // Poll immediately and then at interval
    checkFileStatuses(token, submissionId);
    pollingIntervalRef.current = setInterval(
      () => checkFileStatuses(token, submissionId),
      pollingInterval
    );
  };

  const ensureWidgetSubmission = async () => {
    if (widgetSubmissionIdRef.current) {
      return widgetSubmissionIdRef.current;
    }

    const submissionResponse = await createWidgetSubmission(coreToken);

    if (submissionResponse.error || !submissionResponse.data) {
      console.error('Failed to create widget submission', submissionResponse);
      return null;
    }

    const widgetSubmissionId = get(submissionResponse, 'data.data.id');

    if (!widgetSubmissionId) {
      console.error(
        'Widget submission ID not found in response',
        submissionResponse
      );
      return null;
    }

    widgetSubmissionIdRef.current = widgetSubmissionId;
    return widgetSubmissionId;
  };

  const updateFileStatusForTempId = (tempId, uploadId, status = 'pending', fileId = null) => {
    tempIdToUploadIdMapRef.current = {
      ...tempIdToUploadIdMapRef.current,
      [tempId]: uploadId,
    };

    setFileStatuses((prevStatuses) => {
      const existingIndex = prevStatuses.findIndex(
        (fs) => fs.tempId === tempId
      );

      const newFileStatus = {
        tempId,
        serverId: uploadId,
        status,
        ...(fileId && { fileId }),
      };

      if (existingIndex >= 0) {
        const updated = [...prevStatuses];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...newFileStatus,
        };
        return updated;
      }
      return [...prevStatuses, newFileStatus];
    });
  };

  const processUploadResponse = (response, file) => {
    const fileId = getFileId(file);

    if (response.error || !response.data) {
      // Update status to error if upload failed
      if (file.tempId) {
        setFileStatuses((prevStatuses) => {
          const existingIndex = prevStatuses.findIndex(
            (fs) => fs.tempId === file.tempId
          );
          if (existingIndex >= 0) {
            const updated = [...prevStatuses];
            updated[existingIndex] = {
              ...updated[existingIndex],
              status: 'error',
            };
            return updated;
          }
          return prevStatuses;
        });
      }
      return;
    }

    uploadedFilesRef.current.add(fileId);

    const uploadId = get(response, 'data.data.id', null);
    if (!uploadId) {
      return;
    }

    fileUploadStatusesRef.current = {
      ...fileUploadStatusesRef.current,
      [uploadId]: 'pending',
    };
    setHasPendingFiles(true);

    if (file.tempId) {
      updateFileStatusForTempId(file.tempId, uploadId, 'pending', fileId);
    }
  };

  const filterNewFiles = (fileOrFiles) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    return files.filter((file) => {
      if (!file) return false;
      const fileId = getFileId(file);
      return !uploadedFilesRef.current.has(fileId);
    });
  };

  const uploadFiles = async (newFiles, widgetSubmissionId) => {
    const uploadPromises = newFiles.map((file) =>
      uploadFile(coreToken, file, widgetSubmissionId, recordType)
    );

    const uploadResponses = await Promise.all(uploadPromises);

    uploadResponses.forEach((response, index) => {
      processUploadResponse(response, newFiles[index]);
    });

    return uploadResponses;
  };

  const logUploadResults = (uploadResponses, newFilesCount) => {
    const failedUploads = uploadResponses.filter(
      (response) => response.error || !response.data
    );

    if (failedUploads.length > 0) {
      console.error('Some files failed to upload', failedUploads);
    } else {
      console.log(`Successfully uploaded ${newFilesCount} file(s)`);
    }
  };

  const initializeFileStatuses = (files) => {
    files.forEach((file) => {
      if (file.tempId) {
        const fileId = getFileId(file);
        setFileStatuses((prevStatuses) => {
          const existingIndex = prevStatuses.findIndex(
            (fs) => fs.tempId === file.tempId
          );

          const uploadingStatus = {
            tempId: file.tempId,
            serverId: null,
            status: 'uploading',
            fileId,
          };

          if (existingIndex >= 0) {
            const updated = [...prevStatuses];
            updated[existingIndex] = uploadingStatus;
            return updated;
          }
          return [...prevStatuses, uploadingStatus];
        });
      }
    });
  };

  const removeFilesFromStatus = (files) => {
    files.forEach((file) => {
      if (file.tempId) {
        setFileStatuses((prevStatuses) =>
          prevStatuses.filter((fs) => fs.tempId !== file.tempId)
        );
      }
    });
  };

  const updateFilesToErrorStatus = (files) => {
    files.forEach((file) => {
      if (file.tempId) {
        setFileStatuses((prevStatuses) => {
          const existingIndex = prevStatuses.findIndex(
            (fs) => fs.tempId === file.tempId
          );
          if (existingIndex >= 0) {
            const updated = [...prevStatuses];
            updated[existingIndex] = {
              ...updated[existingIndex],
              status: 'error',
            };
            return updated;
          }
          return prevStatuses;
        });
      }
    });
  };

  const handleFileSelect = async (fileOrFiles) => {
    if (!coreToken) {
      console.error('Core token not available');
      return;
    }

    const newFiles = filterNewFiles(fileOrFiles);

    if (newFiles.length === 0) {
      console.log('All selected files have already been uploaded');
      return;
    }

    initializeFileStatuses(newFiles);
    setIsUploadingFiles(true);

    try {
      const widgetSubmissionId = await ensureWidgetSubmission();
      if (!widgetSubmissionId) {
        removeFilesFromStatus(newFiles);
        return;
      }

      const uploadResponses = await uploadFiles(newFiles, widgetSubmissionId);
      logUploadResults(uploadResponses, newFiles.length);
    } catch (error) {
      console.error('Error handling file upload', error);
      updateFilesToErrorStatus(newFiles);
    } finally {
      setIsUploadingFiles(false);
      startPolling(coreToken, widgetSubmissionIdRef.current);
    }
  };

  const handleFileRemove = async (tempId) => {
    if (!coreToken) {
      console.error('Core token not available');
      return null;
    }

    // Get serverId from tempId mapping
    const serverId = tempIdToUploadIdMapRef.current[tempId];

    if (!serverId) {
      console.warn(`No serverId found for tempId: ${tempId}`);
      return null;
    }

    try {
      const response = await deleteFileUpload(coreToken, serverId);

      if (response.error) {
        console.error('Failed to delete file upload', response);
        return response;
      }

      // Remove from fileStatuses and uploadedFilesRef
      setFileStatuses((prevStatuses) => {
        const fileStatus = prevStatuses.find((fs) => fs.tempId === tempId);
        if (fileStatus?.fileId) {
          uploadedFilesRef.current.delete(fileStatus.fileId);
        }
        return prevStatuses.filter((fileStatus) => fileStatus.tempId !== tempId);
      });

      // Remove from fileUploadStatusesRef
      const { [serverId]: removed, ...rest } = fileUploadStatusesRef.current;
      fileUploadStatusesRef.current = rest;

      // Remove from tempIdToUploadIdMapRef
      const { [tempId]: removedTemp, ...restTemp } = tempIdToUploadIdMapRef.current;
      tempIdToUploadIdMapRef.current = restTemp;

      // Check if there are still pending files
      const hasPending = Object.values(fileUploadStatusesRef.current).some(
        (status) => status === 'pending'
      );
      setHasPendingFiles(hasPending);

      // Stop polling if no pending files
      if (!hasPending) {
        stopPolling();
      }

      return response;
    } catch (error) {
      console.error('Error removing file upload', error);
      return { error };
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const getWidgetSubmissionId = () => widgetSubmissionIdRef.current;

  return {
    handleFileSelect,
    handleFileRemove,
    hasPendingFiles,
    isUploadingFiles,
    getWidgetSubmissionId,
    fileStatuses,
  };
};

export default useFileUpload;
